import { cn } from '@/utils/cn';
import type { InputHTMLAttributes, ChangeEvent } from 'react';
import { forwardRef, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { TimePicker } from '@/components/ui/TimePicker';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint: _hint, className, id, type, value, onChange, disabled, required, name, ...props }, ref) => {
    const inputId = id ?? name;
    const { required: isRequired } = parseFieldLabel(label, required);
    const isPassword = type === 'password';
    const [showPassword, setShowPassword] = useState(false);
    const inputType = isPassword && showPassword ? 'text' : type;

    if (type === 'time') {
      return (
        <TimePicker
          label={label}
          error={error}
          hint={_hint}
          className={className}
          id={inputId}
          name={name}
          disabled={disabled}
          required={isRequired}
          value={typeof value === 'string' ? value : ''}
          onChange={(next) => {
            onChange?.({
              target: { value: next, name: name ?? '' },
            } as ChangeEvent<HTMLInputElement>);
          }}
        />
      );
    }

    return (
      <div className="w-full">
        <FieldLabel htmlFor={inputId} label={label} required={required} />
        <div className={isPassword ? 'relative' : undefined}>
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={isRequired}
            className={cn('input', error && 'input-error', isPassword && 'pr-11', className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${inputId}-error` : undefined}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? (
                <VisibilityOffIcon sx={{ fontSize: 20 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 20 }} />
              )}
            </button>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
